import React, { useState } from 'react';
import Select, { components as selectComponents } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { FaPlus, FaTrash, FaUser, FaPills, FaInfoCircle, FaLeaf, FaFlask, FaHeartbeat, FaChevronDown, FaRobot } from 'react-icons/fa';
import { 
  GENDER_OPTIONS, 
  LIFE_EXPECTANCY_OPTIONS, 
  DURATION_OPTIONS, 
  COMORBIDITIES, 
  CFS_DESCRIPTIONS 
} from '../utils/constants';
import {
  COMMON_MEDICATIONS,
  COMMON_HERBS,
  FREQUENCY_OPTIONS,
  INDICATION_OPTIONS,
  HERBAL_EFFECTS
} from '../utils/medicationOptions';
import MedicationExtractor from './MedicationExtractor';


const PatientForm = ({ onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    age: '',
    gender: 'female',
    is_frail: false,
    cfs_score: '',
    life_expectancy: '2-5_years',
    comorbidities: [],
    medications: [],
    herbs: [],
    serum_creatinine_mg_dl: null,
    bilirubin_mg_dl: null,
    inr: null,
    ast: null,
    alt: null,
    sodium: null
  });

  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    labs: false,
    meds: true,
    herbs: false,
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleExtractedMeds = (extractedMeds) => {
    setFormData(prev => ({
      ...prev,
      medications: [...prev.medications, ...extractedMeds]
    }));
    alert(`Added ${extractedMeds.length} medication(s)! Review and edit them below if needed.`);
  };

  const [currentMedication, setCurrentMedication] = useState({
    generic_name: '',
    brand_name: '',
    dose: '',
    frequency: '',
    indication: '',
    duration: 'long_term'
  });

  const [currentHerb, setCurrentHerb] = useState({
    generic_name: '',
    brand_name: '',
    dose: '',
    frequency: '',
    intended_effect: '',
    duration: 'long_term'
  });
  
  const comorbidityOptions = COMORBIDITIES.map((c) => ({ value: c, label: c }));
  const [currentComorbidity, setCurrentComorbidity] = useState(null);

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: '#000000',
      borderWidth: '2px',
      boxShadow: state.isFocused
        ? '4px 4px 0px 0px rgba(0,0,0,1)'
        : '2px 2px 0px 0px rgba(0,0,0,1)',
      '&:hover': {
        borderColor: '#000000',
        boxShadow: '4px 4px 0px 0px rgba(0,0,0,1)'
      },
      minHeight: '48px',
      borderRadius: '0.75rem', // rounded-xl
      backgroundColor: '#ffffff',
      color: '#111827',
      fontWeight: '700',
      transition: 'all 0.2s ease',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? '#dbeafe'
        : state.isFocused
        ? '#f1f5f9'
        : '#ffffff',
      color: '#111827',
      padding: '12px 16px',
      cursor: 'pointer',
      fontWeight: '600',
      borderBottom: '1px solid #e5e7eb',
      '&:active': {
        backgroundColor: '#bfdbfe',
        color: '#111827'
      }
    }),
    menu: (base) => ({
      ...base,
      zIndex: 100,
      borderRadius: '0.75rem',
      boxShadow: '4px 4px 0px 0px rgba(0,0,0,1)',
      border: '2px solid #000000',
      overflow: 'hidden',
      marginTop: '8px'
    }),
    placeholder: (base) => ({
      ...base,
      color: '#6b7280',
      fontWeight: '500'
    }),
    singleValue: (base) => ({
      ...base,
      color: '#111827',
      fontWeight: '700'
    }),
    noOptionsMessage: (base) => ({
      ...base,
      padding: '12px 16px',
      color: '#111827',
      fontWeight: 600
    })
  };

  const herbSelectStyles = {
    ...selectStyles,
  };

  const DropdownIndicator = (props) => (
    <selectComponents.DropdownIndicator {...props}>
      <FaChevronDown />
    </selectComponents.DropdownIndicator>
  );

  const indicatorComponents = {
    DropdownIndicator,
    IndicatorSeparator: null,
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const addComorbidity = () => {
    const value = currentComorbidity?.value;
    if (!value) return;

    if (value === 'Unknown') {
      setFormData({ ...formData, comorbidities: ['Unknown'] });
      setCurrentComorbidity(null);
      return;
    }

    const withoutUnknown = formData.comorbidities.filter((c) => c !== 'Unknown');
    if (withoutUnknown.includes(value)) {
      setCurrentComorbidity(null);
      return;
    }

    setFormData({
      ...formData,
      comorbidities: [...withoutUnknown, value],
    });
    setCurrentComorbidity(null);
  };

  const removeComorbidity = (comorbidity) => {
    setFormData({
      ...formData,
      comorbidities: formData.comorbidities.filter((c) => c !== comorbidity),
    });
  };

  const addMedication = () => {
    if (currentMedication.generic_name && currentMedication.dose) {
      setFormData({
        ...formData,
        medications: [...formData.medications, currentMedication]
      });
      setCurrentMedication({
        generic_name: '',
        brand_name: '',
        dose: '',
        frequency: '',
        indication: '',
        duration: 'long_term'
      });
    } else {
      alert('Please enter at least the generic name and dose');
    }
  };

  const removeMedication = (index) => {
    setFormData({
      ...formData,
      medications: formData.medications.filter((_, i) => i !== index)
    });
  };

  const addHerb = () => {
    if (currentHerb.generic_name) {
      setFormData({
        ...formData,
        herbs: [...formData.herbs, currentHerb]
      });
      setCurrentHerb({
        generic_name: '',
        brand_name: '',
        dose: '',
        frequency: '',
        intended_effect: '',
        duration: 'long_term'
      });
    } else {
      alert('Please enter the herb name');
    }
  };

  const removeHerb = (index) => {
    setFormData({
      ...formData,
      herbs: formData.herbs.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.age || formData.medications.length === 0) {
      alert('Please fill in age and add at least one medication');
      return;
    }

    // Transform data to match backend schema
    const submissionData = {
      ...formData,
      age: parseInt(formData.age, 10),
      cfs_score: formData.cfs_score ? parseInt(formData.cfs_score, 10) : null,
      serum_bilirubin_mg_dl: formData.bilirubin_mg_dl ? parseFloat(formData.bilirubin_mg_dl) : null,
      ast_u_l: formData.ast ? parseFloat(formData.ast) : null,
      alt_u_l: formData.alt ? parseFloat(formData.alt) : null,
      serum_sodium_mmol_l: formData.sodium ? parseFloat(formData.sodium) : null,
      serum_creatinine_mg_dl: formData.serum_creatinine_mg_dl ? parseFloat(formData.serum_creatinine_mg_dl) : null,
      inr: formData.inr ? parseFloat(formData.inr) : null,
      // Remove frontend-specific keys that don't match backend model to avoid potential strict validation issues
      bilirubin_mg_dl: undefined,
      ast: undefined,
      alt: undefined,
      sodium: undefined
    };

    onSubmit(submissionData);
  };

  const SectionHeader = ({ icon: Icon, title, section, count, color = "neo-accent" }) => {
    const isExpanded = expandedSections[section];
    
    return (
      <button
        type="button"
        onClick={() => toggleSection(section)}
        className={`w-full flex items-center justify-between p-4 rounded-xl transition-all duration-300 border-2 border-neo-main
                  ${isExpanded 
                    ? 'bg-neo-accent text-white shadow-neo-brutal' 
                    : 'bg-white hover:bg-neo-bg text-neo-text shadow-neo-sm hover:translate-x-[2px] hover:translate-y-[2px]'}`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center border-2 border-neo-main
                        ${isExpanded ? 'bg-white text-neo-main' : 'bg-neo-accent text-white'}`}>
            <Icon />
          </div>
          <div className="text-left">
            <h3 className="font-black text-lg">{title}</h3>
            {count !== undefined && (
              <p className={`text-sm font-medium ${isExpanded ? 'text-white' : 'text-neo-text-sub'}`}>
                {count} item(s) added
              </p>
            )}
          </div>
        </div>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-transform duration-300 border-2 border-neo-main
                      ${isExpanded ? 'bg-white text-neo-main rotate-180' : 'bg-neo-bg text-neo-main'}`}>
          <FaChevronDown />
        </div>
      </button>
    );
  };

  return (
    <div className="neo-card p-6 lg:p-8 mb-6 animate-fade-in-up bg-neo-card">
      <MedicationExtractor onMedicationsExtracted={handleExtractedMeds} />
      
      <div className="flex items-center gap-4 mb-8 border-b-2 border-neo-main pb-6">
        <div className="w-16 h-16 bg-neo-accent text-white rounded-lg border-2 border-neo-main 
                      flex items-center justify-center shadow-neo-brutal transform -rotate-2">
          <FaUser className="text-3xl" />
        </div>
        <div>
          <h2 className="text-3xl font-black text-neo-main uppercase tracking-tight">Patient Information</h2>
          <p className="text-neo-text-sub font-bold">Enter patient details for comprehensive analysis</p>
        </div>
      </div>

      <div className="bg-neo-bg border-2 border-neo-main p-5 mb-8 rounded-lg shadow-neo-sm">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-white text-neo-accent rounded-lg border-2 border-neo-main
                        flex items-center justify-center flex-shrink-0 shadow-neo-sm">
            <FaInfoCircle className="text-xl" />
          </div>
          <div>
            <h4 className="font-black text-neo-main mb-2 text-lg">QUICK TIPS</h4>
            <ul className="text-sm text-neo-text font-medium space-y-1.5">
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-neo-accent border border-neo-main rounded-none"></div>
                <strong>Search medications:</strong> Start typing to see suggestions
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-neo-accent border border-neo-main rounded-none"></div>
                <strong>Add custom:</strong> Type any name and press Enter if not in list
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-neo-accent border border-neo-main rounded-none"></div>
                <strong>Brand names:</strong> Optional - skip if unknown
              </li>
            </ul>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          <SectionHeader icon={FaHeartbeat} title="Basic Patient Information" section="basic" color="cyan" />
          
          {expandedSections.basic && (
            <div className="animate-fade-in p-6 bg-white border-2 border-neo-main rounded-xl space-y-6 shadow-neo-brutal">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-bold text-neo-main mb-2 uppercase tracking-wide">
                    Age <span className="text-neo-danger">*</span>
                  </label>
                  <input
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleInputChange}
                    className="neo-input font-bold"
                    placeholder="e.g., 75"
                    min="1"
                    max="120"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-neo-main mb-2 uppercase tracking-wide">
                    Gender <span className="text-neo-danger">*</span>
                  </label>
                  <Select
                    options={GENDER_OPTIONS}
                    value={GENDER_OPTIONS.find((o) => o.value === formData.gender) || null}
                    onChange={(option) => setFormData((prev) => ({ ...prev, gender: option?.value || 'female' }))}
                    isSearchable={false}
                    styles={selectStyles}
                    components={indicatorComponents}
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-neo-main mb-2 uppercase tracking-wide">
                    Clinical Frailty Scale (CFS)
                  </label>
                  <Select
                    options={Object.entries(CFS_DESCRIPTIONS).map(([score, description]) => ({
                      value: score,
                      label: `${score} - ${description}`,
                    }))}
                    value={
                      formData.cfs_score
                        ? {
                            value: formData.cfs_score,
                            label: CFS_DESCRIPTIONS[formData.cfs_score]
                              ? `${formData.cfs_score} - ${CFS_DESCRIPTIONS[formData.cfs_score]}`
                              : `${formData.cfs_score}`,
                          }
                        : null
                    }
                    onChange={(option) => setFormData((prev) => ({ ...prev, cfs_score: option?.value || '' }))}
                    isClearable
                    placeholder="Not assessed"
                    styles={selectStyles}
                    components={indicatorComponents}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-neo-main mb-2 uppercase tracking-wide">
                    Life Expectancy <span className="text-neo-danger">*</span>
                  </label>
                  <Select
                    options={LIFE_EXPECTANCY_OPTIONS}
                    value={LIFE_EXPECTANCY_OPTIONS.find((o) => o.value === formData.life_expectancy) || null}
                    onChange={(option) =>
                      setFormData((prev) => ({ ...prev, life_expectancy: option?.value || '2-5_years' }))
                    }
                    isSearchable={false}
                    styles={selectStyles}
                    components={indicatorComponents}
                  />
                </div>

                <div className="flex items-center h-full pt-6">
                  <label className="flex items-center gap-3 cursor-pointer group w-full p-2 rounded-md hover:bg-neo-bg transition-colors border-2 border-transparent hover:border-neo-main">
                    <div className="relative">
                      <input
                        type="checkbox"
                        name="is_frail"
                        checked={formData.is_frail}
                        onChange={handleInputChange}
                        className="sr-only peer"
                      />
                      <div className="w-6 h-6 border-2 border-neo-main rounded bg-white peer-checked:bg-neo-accent transition-all duration-200 
                                    flex items-center justify-center shadow-neo-sm">
                        <svg className="w-4 h-4 text-white opacity-0 peer-checked:opacity-100" 
                             fill="currentColor" viewBox="0 0 20 20">
                          <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                        </svg>
                      </div>
                    </div>
                    <span className="text-neo-main font-bold">Patient is clinically frail</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-neo-main mb-3 uppercase tracking-wide">
                  Comorbidities
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                  <div className="md:col-span-2">
                    <CreatableSelect
                      options={comorbidityOptions}
                      value={currentComorbidity}
                      onChange={(selected) => setCurrentComorbidity(selected || null)}
                      onCreateOption={(inputValue) => {
                        const trimmed = String(inputValue || "").trim();
                        if (!trimmed) return;
                        setCurrentComorbidity({ value: trimmed, label: trimmed });
                      }}
                      isClearable
                      isSearchable
                      placeholder="Select comorbidity..."
                      styles={selectStyles}
                      components={indicatorComponents}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={addComorbidity}
                    className="w-full px-6 py-3 bg-white text-neo-main font-black rounded-xl 
                             border-2 border-neo-main shadow-neo-brutal hover:shadow-neo-sm hover:bg-neo-accent hover:text-white
                             hover:translate-x-[2px] hover:translate-y-[2px]
                             transition-all duration-300 flex items-center justify-center gap-2 uppercase tracking-wide"
                  >
                    <FaPlus />
                    Add
                  </button>
                </div>

                {formData.comorbidities.length > 0 && (
                  <div className="mt-6 space-y-3">
                    {formData.comorbidities.map((comorbidity) => (
                      <div
                        key={comorbidity}
                        className="flex items-center justify-between bg-white p-4 rounded-xl border-2 border-neo-main
                                 shadow-neo-sm hover:shadow-neo-brutal hover:translate-x-[-2px] hover:translate-y-[-2px]
                                 transition-all duration-200 animate-fade-in group"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-white text-neo-accent rounded-lg border-2 border-neo-main 
                                        flex items-center justify-center shadow-neo-sm group-hover:rotate-3 transition-transform">
                            <FaHeartbeat className="text-xl" />
                          </div>
                          <span className="font-black text-lg text-neo-main block">{comorbidity}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeComorbidity(comorbidity)}
                          className="w-10 h-10 bg-neo-bg hover:bg-neo-danger hover:text-white text-neo-main rounded-lg border-2 border-neo-main
                                   flex items-center justify-center transition-all duration-200 shadow-neo-sm hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px]"
                        >
                          <FaTrash className="text-sm" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <SectionHeader icon={FaFlask} title="Lab Values (Optional)" section="labs" color="purple" />
          
          {expandedSections.labs && (
            <div className="animate-fade-in p-6 bg-white border-2 border-neo-main rounded-xl shadow-neo-brutal">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-bold text-neo-main mb-2 uppercase tracking-wide">
                    Serum Creatinine (mg/dL)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.serum_creatinine_mg_dl || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, serum_creatinine_mg_dl: parseFloat(e.target.value) || null })
                    }
                    className="neo-input font-bold"
                    placeholder="e.g., 1.2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-neo-main mb-2 uppercase tracking-wide">Bilirubin (mg/dL)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.bilirubin_mg_dl || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, bilirubin_mg_dl: parseFloat(e.target.value) || null })
                    }
                    className="neo-input font-bold"
                    placeholder="e.g., 0.8"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-neo-main mb-2 uppercase tracking-wide">INR</label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.inr || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, inr: parseFloat(e.target.value) || null })
                    }
                    className="neo-input font-bold"
                    placeholder="e.g., 1.1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-neo-main mb-2 uppercase tracking-wide">AST (U/L)</label>
                  <input
                    type="number"
                    value={formData.ast || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, ast: parseFloat(e.target.value) || null })
                    }
                    className="neo-input font-bold"
                    placeholder="e.g., 22"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-neo-main mb-2 uppercase tracking-wide">ALT (U/L)</label>
                  <input
                    type="number"
                    value={formData.alt || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, alt: parseFloat(e.target.value) || null })
                    }
                    className="neo-input font-bold"
                    placeholder="e.g., 18"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-neo-main mb-2 uppercase tracking-wide">Sodium (mmol/L)</label>
                  <input
                    type="number"
                    value={formData.sodium || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, sodium: parseFloat(e.target.value) || null })
                    }
                    className="neo-input font-bold"
                    placeholder="e.g., 138"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <SectionHeader 
            icon={FaPills} 
            title="Allopathic Medications" 
            section="meds" 
            count={formData.medications.length}
            color="blue" 
          />
          
          {expandedSections.meds && (
            <div className="animate-fade-in p-6 bg-white border-2 border-neo-main rounded-xl space-y-6 shadow-neo-brutal">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1">
                  <label className="block text-sm font-bold text-neo-main mb-2 uppercase tracking-wide">
                    Generic Name <span className="text-neo-danger">*</span>
                  </label>
                  <CreatableSelect
                    options={COMMON_MEDICATIONS}
                    value={currentMedication.generic_name ? {
                      value: currentMedication.generic_name,
                      label: currentMedication.generic_name
                    } : null}
                    onChange={(selected) => setCurrentMedication({
                      ...currentMedication,
                      generic_name: selected ? selected.value : ''
                    })}
                    onCreateOption={(inputValue) => {
                      setCurrentMedication({
                        ...currentMedication,
                        generic_name: inputValue
                      });
                    }}
                    isClearable
                    isSearchable
                    placeholder="Type medication name..."
                    styles={selectStyles}
                    formatCreateLabel={(inputValue) => `Add: "${inputValue}"`}
                    noOptionsMessage={({ inputValue }) => 
                      inputValue ? `Press Enter to add "${inputValue}"` : "Start typing..."
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-neo-main mb-2 uppercase tracking-wide">
                    Brand Name <span className="text-neo-text-sub text-xs lowercase font-normal ml-1">(optional)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Xanax"
                    value={currentMedication.brand_name}
                    onChange={(e) => setCurrentMedication({...currentMedication, brand_name: e.target.value})}
                    className="neo-input font-bold"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-neo-main mb-2 uppercase tracking-wide">
                    Dose <span className="text-neo-danger">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., 20mg"
                    value={currentMedication.dose}
                    onChange={(e) => setCurrentMedication({...currentMedication, dose: e.target.value})}
                    className="neo-input font-bold"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-neo-main mb-2 uppercase tracking-wide">
                    Frequency
                  </label>
                  <CreatableSelect
                    options={FREQUENCY_OPTIONS}
                    value={currentMedication.frequency ? {
                      value: currentMedication.frequency,
                      label: currentMedication.frequency
                    } : null}
                    onChange={(selected) => setCurrentMedication({
                      ...currentMedication,
                      frequency: selected ? selected.value : ''
                    })}
                    onCreateOption={(inputValue) => {
                      setCurrentMedication({
                        ...currentMedication,
                        frequency: inputValue
                      });
                    }}
                    isClearable
                    isSearchable
                    placeholder="Select or type..."
                    styles={selectStyles}
                    formatCreateLabel={(inputValue) => `Use: "${inputValue}"`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-neo-main mb-2 uppercase tracking-wide">
                    Indication
                  </label>
                  <CreatableSelect
                    options={INDICATION_OPTIONS}
                    value={currentMedication.indication ? {
                      value: currentMedication.indication,
                      label: currentMedication.indication
                    } : null}
                    onChange={(selected) => setCurrentMedication({
                      ...currentMedication,
                      indication: selected ? selected.value : ''
                    })}
                    onCreateOption={(inputValue) => {
                      setCurrentMedication({
                        ...currentMedication,
                        indication: inputValue
                      });
                    }}
                    isClearable
                    isSearchable
                    placeholder="Why prescribed?"
                    styles={selectStyles}
                    formatCreateLabel={(inputValue) => `Add: "${inputValue}"`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-neo-main mb-2 uppercase tracking-wide">
                    Duration
                  </label>
                  <div className="relative">
                    <select
                      value={currentMedication.duration}
                      onChange={(e) => setCurrentMedication({...currentMedication, duration: e.target.value})}
                      className="neo-input font-bold appearance-none cursor-pointer"
                    >
                      {DURATION_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-neo-main">
                      <FaChevronDown />
                    </div>
                  </div>
                </div>
              </div>
              
              <button
                type="button"
                onClick={addMedication}
                className="w-full md:w-auto px-8 py-3 bg-neo-accent text-white font-black rounded-xl 
                         border-2 border-neo-main shadow-neo-brutal hover:shadow-neo-sm hover:translate-x-[2px] hover:translate-y-[2px]
                         transition-all duration-300 flex items-center justify-center gap-2 uppercase tracking-wide"
              >
                <FaPlus />
                Add Medication
              </button>

              {formData.medications.length > 0 && (
                <div className="mt-8 pt-6 border-t-2 border-neo-main">
                  <h4 className="text-sm font-black text-neo-main mb-4 flex items-center gap-2 uppercase tracking-wide">
                    <FaPills className="text-neo-accent" />
                    Added Medications ({formData.medications.length})
                  </h4>
                  <div className="space-y-3">
                    {formData.medications.map((med, index) => (
                      <div 
                        key={index} 
                        className="flex items-center justify-between bg-white p-4 rounded-xl border-2 border-neo-main
                                 shadow-neo-sm hover:shadow-neo-brutal hover:translate-x-[-2px] hover:translate-y-[-2px] 
                                 transition-all duration-200 animate-fade-in group"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-neo-accent text-white rounded-lg border-2 border-neo-main 
                                        flex items-center justify-center shadow-neo-sm group-hover:rotate-3 transition-transform">
                            <FaPills className="text-xl" />
                          </div>
                          <div>
                            <span className="font-black text-lg text-neo-main block">{med.generic_name}</span>
                            <div className="flex flex-wrap gap-2 text-sm text-neo-text font-bold">
                              {med.brand_name && <span className="bg-neo-bg px-2 py-0.5 rounded border border-neo-main text-xs">({med.brand_name})</span>}
                              <span>{med.dose}</span>
                              {med.frequency && <span className="text-neo-text-sub">• {med.frequency}</span>}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeMedication(index)}
                          className="w-10 h-10 bg-neo-bg hover:bg-neo-danger hover:text-white text-neo-main rounded-lg border-2 border-neo-main
                                   flex items-center justify-center transition-all duration-200 shadow-neo-sm hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px]"
                        >
                          <FaTrash className="text-sm" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <SectionHeader 
            icon={FaLeaf} 
            title="Ayurvedic / Herbal Products" 
            section="herbs" 
            count={formData.herbs.length}
            color="green" 
          />
          
          {expandedSections.herbs && (
            <div className="animate-fade-in p-6 bg-white border-2 border-neo-main rounded-xl space-y-6 shadow-neo-brutal">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1">
                  <label className="block text-sm font-bold text-neo-main mb-2 uppercase tracking-wide">
                    Herb Name <span className="text-neo-danger">*</span>
                  </label>
                  <CreatableSelect
                    options={COMMON_HERBS}
                    value={currentHerb.generic_name ? {
                      value: currentHerb.generic_name,
                      label: currentHerb.generic_name
                    } : null}
                    onChange={(selected) => setCurrentHerb({
                      ...currentHerb,
                      generic_name: selected ? selected.value : ''
                    })}
                    onCreateOption={(inputValue) => {
                      setCurrentHerb({
                        ...currentHerb,
                        generic_name: inputValue
                      });
                    }}
                    isClearable
                    isSearchable
                    placeholder="Type herb name..."
                    styles={herbSelectStyles}
                    formatCreateLabel={(inputValue) => `Add: "${inputValue}"`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-neo-main mb-2 uppercase tracking-wide">
                    Brand Name <span className="text-neo-text-sub text-xs lowercase font-normal ml-1">(optional)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Product brand"
                    value={currentHerb.brand_name}
                    onChange={(e) => setCurrentHerb({...currentHerb, brand_name: e.target.value})}
                    className="neo-input font-bold"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-neo-main mb-2 uppercase tracking-wide">Dose</label>
                  <input
                    type="text"
                    placeholder="e.g., 500mg"
                    value={currentHerb.dose}
                    onChange={(e) => setCurrentHerb({...currentHerb, dose: e.target.value})}
                    className="neo-input font-bold"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-neo-main mb-2 uppercase tracking-wide">Frequency</label>
                  <CreatableSelect
                    options={FREQUENCY_OPTIONS}
                    value={currentHerb.frequency ? {
                      value: currentHerb.frequency,
                      label: currentHerb.frequency
                    } : null}
                    onChange={(selected) => setCurrentHerb({
                      ...currentHerb,
                      frequency: selected ? selected.value : ''
                    })}
                    onCreateOption={(inputValue) => {
                      setCurrentHerb({
                        ...currentHerb,
                        frequency: inputValue
                      });
                    }}
                    isClearable
                    isSearchable
                    placeholder="Select..."
                    styles={herbSelectStyles}
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-neo-main mb-2 uppercase tracking-wide">Intended Effect</label>
                  <CreatableSelect
                    options={HERBAL_EFFECTS}
                    value={currentHerb.intended_effect ? {
                      value: currentHerb.intended_effect,
                      label: currentHerb.intended_effect
                    } : null}
                    onChange={(selected) => setCurrentHerb({
                      ...currentHerb,
                      intended_effect: selected ? selected.value : ''
                    })}
                    onCreateOption={(inputValue) => {
                      setCurrentHerb({
                        ...currentHerb,
                        intended_effect: inputValue
                      });
                    }}
                    isClearable
                    isSearchable
                    placeholder="What is it for?"
                    styles={herbSelectStyles}
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-neo-main mb-2 uppercase tracking-wide">Duration</label>
                  <div className="relative">
                    <select
                      value={currentHerb.duration}
                      onChange={(e) => setCurrentHerb({...currentHerb, duration: e.target.value})}
                      className="neo-input font-bold appearance-none cursor-pointer"
                    >
                      {DURATION_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-neo-main">
                      <FaChevronDown />
                    </div>
                  </div>
                </div>
              </div>
              
              <button
                type="button"
                onClick={addHerb}
                className="w-full md:w-auto px-8 py-3 bg-white text-neo-main font-black rounded-xl 
                         border-2 border-neo-main shadow-neo-brutal hover:shadow-neo-sm hover:bg-neo-accent hover:text-white
                         hover:translate-x-[2px] hover:translate-y-[2px]
                         transition-all duration-300 flex items-center justify-center gap-2 uppercase tracking-wide"
              >
                <FaPlus />
                Add Herb
              </button>

              {formData.herbs.length > 0 && (
                <div className="mt-8 pt-6 border-t-2 border-neo-main">
                  <h4 className="text-sm font-black text-neo-main mb-4 flex items-center gap-2 uppercase tracking-wide">
                    <FaLeaf className="text-neo-accent" />
                    Added Herbs ({formData.herbs.length})
                  </h4>
                  <div className="space-y-3">
                    {formData.herbs.map((herb, index) => (
                      <div 
                        key={index} 
                        className="flex items-center justify-between bg-white p-4 rounded-xl border-2 border-neo-main
                                 shadow-neo-sm hover:shadow-neo-brutal hover:translate-x-[-2px] hover:translate-y-[-2px]
                                 transition-all duration-200 animate-fade-in group"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-white text-neo-accent rounded-lg border-2 border-neo-main 
                                        flex items-center justify-center shadow-neo-sm group-hover:rotate-3 transition-transform">
                            <FaLeaf className="text-xl" />
                          </div>
                          <div>
                            <span className="font-black text-lg text-neo-main block">{herb.generic_name}</span>
                            <div className="flex flex-wrap gap-2 text-sm text-neo-text font-bold">
                              {herb.brand_name && <span className="bg-neo-bg px-2 py-0.5 rounded border border-neo-main text-xs">({herb.brand_name})</span>}
                              <span>{herb.dose || 'No dose'}</span>
                              {herb.frequency && <span className="text-neo-text-sub">• {herb.frequency}</span>}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeHerb(index)}
                          className="w-10 h-10 bg-neo-bg hover:bg-neo-danger hover:text-white text-neo-main rounded-lg border-2 border-neo-main
                                   flex items-center justify-center transition-all duration-200 shadow-neo-sm hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px]"
                        >
                          <FaTrash className="text-sm" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="pt-8 border-t-2 border-neo-main">
          <button
            type="submit"
            disabled={isLoading}
            className={`w-full py-4 rounded-xl font-black text-xl shadow-neo-brutal transition-all duration-300 
                      flex items-center justify-center gap-3 border-2 border-neo-main uppercase tracking-wide ${
              isLoading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-neo-main text-white hover:bg-neo-accent hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-neo-sm'
            }`}
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
                Analyzing...
              </>
            ) : (
              <>
                <FaRobot className="text-xl" />
                Analyze Patient with AI
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PatientForm;
